import { useState } from 'react';
import type { Thread } from '../../lib/api';
import { useAuthStore } from '../../stores/auth';
import { useMobile } from '../../hooks/useMobile';
import { typeLabel, badgeStyle } from '../../lib/threadLabels';
import { Avatar, AvatarStack, type AvatarTone } from '../ui/Avatar';
import ThreadSheet from './ThreadSheet';
import { Info } from '../../lib/icons';

interface Props {
  thread: Thread | null;
  onBack?: () => void;
  onOpenAddTeammate?: () => void;
}

function avatarTone(type: string): AvatarTone {
  if (type === 'client') return 'client';
  if (type === 'org') return 'org';
  if (type === 'direct') return 'direct';
  return 'default';
}

export default function ChatHeader({ thread, onBack, onOpenAddTeammate }: Props) {
  const currentUserId = useAuthStore((s) => s.user?.id);
  const isMobile = useMobile();
  const [sheetOpen, setSheetOpen] = useState(false);
  if (!thread) return null;

  const contact = thread.type === 'client' ? thread.contact : null;
  const company = contact?.company ?? thread.client_account_name ?? null;

  let displayName = thread.name ?? thread.client_account_name ?? typeLabel[thread.type] ?? 'Thread';
  if (contact) {
    displayName = contact.display_name || contact.name || displayName;
  }
  let dmOtherParticipant = null;
  if (thread.type === 'direct' && thread.participants) {
    const other = thread.participants.find((p) => p.user_id !== currentUserId);
    if (other) {
      displayName = other.name ?? displayName;
      dmOtherParticipant = other;
    }
  }

  const participantCount = thread.participants?.length ?? 0;

  const nameBlock = (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{
        fontSize: '0.92rem', fontWeight: 700, color: 'var(--shell-text)',
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {displayName}
      </span>
      <div style={{ fontSize: '0.72rem', color: 'var(--shell-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
        {company && (
          <span style={{
            fontWeight: 600, padding: '1px 6px', borderRadius: 3,
            background: 'rgba(29,158,117,.15)', color: 'var(--v2)',
            maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {company}
          </span>
        )}
        {!isMobile && (
          <span>
            {thread.type === 'direct' && dmOtherParticipant
              ? dmOtherParticipant.email ?? 'Direct Message'
              : participantCount > 0
                ? `${participantCount} participant${participantCount !== 1 ? 's' : ''}`
                : typeLabel[thread.type]}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <div style={{
      padding: '11px 20px', borderBottom: '1px solid var(--shell-border)',
      background: 'var(--vs1)', display: 'flex', alignItems: 'center',
      gap: 12, flexShrink: 0,
    }}>
      {onBack && (
        <button
          onClick={onBack}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--shell-muted)', padding: '4px 2px', flexShrink: 0,
            display: 'flex', alignItems: 'center', fontSize: '1.1rem', lineHeight: 1,
          }}
          aria-label="Back"
        >
          ←
        </button>
      )}

      <Avatar name={displayName} isOrg={thread.type === 'org'} tone={avatarTone(thread.type)} size={36} />

      {nameBlock}

      {isMobile ? (
        <button
          onClick={() => setSheetOpen(true)}
          style={{
            flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4,
            background: 'var(--hover)', border: '1px solid var(--border-subtle)', borderRadius: 6,
            padding: '6px 10px', color: 'var(--shell-text)', fontSize: '0.76rem', fontWeight: 600, cursor: 'pointer',
          }}
        >
          Thread <Info size={13} strokeWidth={1.8} />
        </button>
      ) : (
        <>
          {thread.type !== 'direct' && (
            <AvatarStack names={(thread.participants ?? []).map((p) => p.name ?? p.contact_name)} />
          )}
          <div style={{ width: 1, height: 28, background: 'var(--border-strong)', flexShrink: 0 }} />
          <span style={{
            fontSize: '0.68rem', fontWeight: 600, padding: '3px 8px', borderRadius: 2, flexShrink: 0,
            ...badgeStyle[thread.type],
          }}>
            {typeLabel[thread.type]?.toUpperCase()}
          </span>
          <button
            onClick={() => setSheetOpen(true)}
            title="Thread details, participants, leave"
            aria-label="Thread details"
            style={{
              flexShrink: 0, display: 'flex', alignItems: 'center',
              background: 'none', border: 'none', color: 'var(--shell-xs)', cursor: 'pointer', padding: 2,
            }}
          >
            <Info size={15} strokeWidth={1.8} />
          </button>
        </>
      )}

      {sheetOpen && (
        <ThreadSheet
          thread={thread}
          displayName={displayName}
          onOpenAddTeammate={onOpenAddTeammate}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </div>
  );
}
